import { apiGet, apiPost } from '../../../adapters/api-client/api';

/**
 * Liveness (TCH-04-FR-05) and background check (TCH-03-FR-08). Mirrors
 * backend/src/contracts/teacher-verification.contract.ts.
 *
 * Provider decision (CTO, 12 Oct 2026): AWS Rekognition Face Liveness for the
 * selfie, Truora for LatAm background checks.
 */

export type LivenessState = 'NOT_STARTED' | 'PENDING' | 'PASSED' | 'FAILED' | 'EXPIRED';

export type BackgroundCheckState =
  | 'NOT_STARTED'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'NEEDS_RESUBMIT'
  | 'CLEARED'
  | 'REJECTED'
  | 'EXPIRED';

export type JurisdictionVerificationState =
  | 'UNCONFIGURED'
  | 'MANUAL_VERIFIABLE'
  | 'PROVIDER_SUPPORTED'
  | 'UNAVAILABLE';

export type BackgroundRenewalStage =
  | 'NOT_APPLICABLE'
  | 'CURRENT'
  | 'NOTICE_FIRST'
  | 'NOTICE_SECOND'
  | 'RENEWAL_DUE'
  | 'EXPIRED';

export interface LivenessFact {
  teacherId: string;
  state: LivenessState;
  provider?: 'rekognition' | 'stub';
  confidence?: number;
  threshold?: number;
  verifiedAt?: string;
  lastAttemptAt?: string;
}

export interface BackgroundCheckFact {
  teacherId: string;
  state: BackgroundCheckState;
  method?: 'OFFICIAL_RECORD_MANUAL' | 'PROVIDER_API';
  jurisdiction: { country?: string; state: JurisdictionVerificationState };
  provider?: string;
  clearedAt?: string;
  expiresAt?: string;
  renewalStage: BackgroundRenewalStage;
  notices: Array<{ kind: 'T-60' | 'T-30' | 'T-7'; daysBeforeExpiry: number; dueAt: string }>;
  safetyRejected: boolean;
  renewalInProgress: boolean;
  reasonCode?: string;
}

export interface MyVerification {
  liveness: LivenessFact;
  backgroundCheck: BackgroundCheckFact;
}

export type StartedLiveness =
  | { sessionId: string; provider: 'rekognition' | 'stub'; region: string }
  | { alreadyPassed: true };

const BASE = '/api/v1/teacher-verification';
const ADMIN_BASE = '/api/v1/admin/teacher-verification';

export function getMyVerification(): Promise<MyVerification> {
  return apiGet(`${BASE}/me`);
}

export function startLiveness(): Promise<StartedLiveness> {
  return apiPost(`${BASE}/me/liveness/sessions`, {});
}

export function completeLiveness(sessionId: string): Promise<{ liveness: LivenessFact }> {
  return apiPost(`${BASE}/me/liveness/sessions/${encodeURIComponent(sessionId)}/complete`, {});
}

export function submitBackgroundCheck(body: {
  nationalId: string;
  consent: boolean;
}): Promise<{ backgroundCheck: BackgroundCheckFact }> {
  return apiPost(`${BASE}/me/background-check`, body);
}

export function refreshBackgroundCheck(): Promise<{ backgroundCheck: BackgroundCheckFact }> {
  return apiPost(`${BASE}/me/background-check/refresh`, {});
}

export interface AdminVerificationFacts extends MyVerification {
  latestAttempt: {
    attemptNumber: number;
    status: BackgroundCheckState;
    method: 'OFFICIAL_RECORD_MANUAL' | 'PROVIDER_API';
    country: string;
    jurisdiction: JurisdictionVerificationState;
    provider?: string;
    reasonCode?: string;
    consentVersion: string;
    consentedAt: string;
    submittedAt: string;
    reviewedBy?: string;
    reviewedAt?: string;
    reviewNote?: string;
  } | null;
}

export function getAdminVerification(teacherId: string): Promise<AdminVerificationFacts> {
  return apiGet(`${ADMIN_BASE}/${encodeURIComponent(teacherId)}`);
}

export function decideBackgroundCheck(
  teacherId: string,
  body: { decision: 'CLEARED' | 'NEEDS_RESUBMIT' | 'REJECTED'; note: string },
): Promise<{ backgroundCheck: BackgroundCheckFact }> {
  return apiPost(`${ADMIN_BASE}/${encodeURIComponent(teacherId)}/background-check/decision`, body);
}
